import { VoxelData, VoxelMap } from "./voxelTypes";

export type Position = { x: number; y: number; z: number }

//
// Position key encoding: Morton code (Z-order curve)
// - Interleaves bits of x, y, z coordinates for better spatial locality
// - Supports coordinates up to 9 bits (0-511) per axis
// - No bias needed - works directly with non-negative coordinates
// - Better cache locality for spatial queries (e.g., flood fill, selection)
// - No bounds parameter required for encoding/decoding
//

/**
 * Maximum coordinate value per axis for Morton encoding (9 bits = 0-511).
 * Document bounds must not exceed this limit.
 */
export const MORTON_MAX_COORD = 511

/**
 * Expand a 9-bit integer by inserting two zeros after each bit.
 * Used for Morton code encoding.
 */
function expandBits(v: number): number {
  let x = v & 0x1ff // 9 bits max (0-511)
  x = (x | (x << 16)) & 0x030000ff
  x = (x | (x << 8)) & 0x0300f00f
  x = (x | (x << 4)) & 0x030c30c3
  x = (x | (x << 2)) & 0x09249249
  return x
}

/**
 * Compact a Morton code coordinate by removing interleaved zeros.
 * Used for Morton code decoding.
 */
function compactBits(x: number): number {
  x = x & 0x09249249
  x = (x ^ (x >> 2)) & 0x030c30c3
  x = (x ^ (x >> 4)) & 0x0300f00f
  x = (x ^ (x >> 8)) & 0x030000ff
  x = (x ^ (x >> 16)) & 0x1ff
  return x
}

/**
 * Encode a Position into an opaque numeric key using Morton code.
 * Document-space coordinates are non-negative (0..511 on each axis).
 */
export const posKey = (p: Position): number => {
  return expandBits(p.x) | (expandBits(p.y) << 1) | (expandBits(p.z) << 2)
}

export const posKeyXYZ = (x: number, y: number, z: number): number => {
  return expandBits(x) | (expandBits(y) << 1) | (expandBits(z) << 2)
}

export const parseKey = (key: number): Position => {
  return {
    x: compactBits(key),
    y: compactBits(key >> 1),
    z: compactBits(key >> 2)
  }
}

export const parseKeyXYZ = (key: number): [number, number, number] => {
  return [
    compactBits(key),
    compactBits(key >> 1),
    compactBits(key >> 2)
  ]
}

/**
 * Canonical pair key for two opaque voxel keys (order-independent),
 * used by selection contour topology to dedupe edges.
 */
export const canonicalPairKey = (a: number, b: number): string => (a <= b ? `${a}|${b}` : `${b}|${a}`)

/**
 * Simple 2D key helper (e.g., for bucketing by (y,z) or (x,y)).
 * String-based for simplicity and stability (coordinates are non-negative).
 */
export const key2D = (a: number, b: number): string => `${a},${b}`

export const posEqual = (a: Position, b: Position): boolean =>
  a.x === b.x && a.y === b.y && a.z === b.z

export const isInBoundsXYZ = (x: number, y: number, z: number, bounds: Position): boolean => {
  return x >= 0 && x < bounds.x &&
    y >= 0 && y < bounds.y &&
    z >= 0 && z < bounds.z
}

export const isInBounds = (pos: Position, bounds: Position): boolean => {
  return isInBoundsXYZ(pos.x, pos.y, pos.z, bounds)
}

export const posAdd = (p1: Position, p2: Position) => (
  {
    x: p1.x + p2.x,
    y: p1.y + p2.y,
    z: p1.z + p2.z
  }
)

export interface BoundingBox {
  min: Position
  max: Position
}


/**
 * Apply a transform to a bounding box
 */
export function boundingBoxOffset(box: BoundingBox, offset: Position): BoundingBox {
  return {
    min: posAdd(box.min, offset),
    max: posAdd(box.max, offset)
  }
}



/**
 * Extract just the X coordinate from a position key (avoiding array allocation)
 */
export const parseKeyX = (key: number): number => {
  return compactBits(key)
}

/**
 * Extract just the Y coordinate from a position key (avoiding array allocation)
 */
export const parseKeyY = (key: number): number => {
  return compactBits(key >> 1)
}

/**
 * Extract just the Z coordinate from a position key (avoiding array allocation)
 */
export const parseKeyZ = (key: number): number => {
  return compactBits(key >> 2)
}

// Morton neighbor stepping helpers (avoid decode/re-encode for ±1 moves)
const X_MASK = 0x09249249
const Y_MASK = 0x12492492
const Z_MASK = 0x24924924

/**
 * Increment/decrement along X in Morton space (wraps within 9-bit lane).
 */
export const incKeyX = (key: number): number => {
  const yz = Y_MASK | Z_MASK
  return (((key | yz) + 1) & ~yz) | (key & yz)
}

export const decKeyX = (key: number): number => {
  const yz = Y_MASK | Z_MASK
  return (((key & ~yz) - 1) & ~yz) | (key & yz)
}

/**
 * Increment/decrement along Y in Morton space (wraps within 9-bit lane).
 */
export const incKeyY = (key: number): number => {
  const xz = X_MASK | Z_MASK
  return (((key | xz) + (1 << 1)) & ~xz) | (key & xz)
}

export const decKeyY = (key: number): number => {
  const xz = X_MASK | Z_MASK
  return (((key & ~xz) - (1 << 1)) & ~xz) | (key & xz)
}

/**
 * Increment/decrement along Z in Morton space (wraps within 9-bit lane).
 */
export const incKeyZ = (key: number): number => {
  const xy = X_MASK | Y_MASK
  return (((key | xy) + (1 << 2)) & ~xy) | (key & xy)
}

export const decKeyZ = (key: number): number => {
  const xy = X_MASK | Y_MASK
  return (((key & ~xy) - (1 << 2)) & ~xy) | (key & xy)
}

// -------------------------------------------------------------------------------------
// Morton stepping utilities (axis helpers + small iteration helpers)
// -------------------------------------------------------------------------------------

export type Axis = 'x' | 'y' | 'z'

/**
 * Select the dominant axis of a vector by largest absolute component.
 * Tie-break behavior matches existing helpers that used strict '>' checks:
 * if no single component is strictly largest, falls through to 'z'.
 */
export function dominantAxis(n: { x: number; y: number; z: number }): Axis {
  const ax = Math.abs(n.x), ay = Math.abs(n.y), az = Math.abs(n.z)
  if (ax > ay && ax > az) return 'x'
  if (ay > az) return 'y'
  return 'z'
}

/**
 * Sign (+1 or -1) along a given axis for the provided vector. Zero maps to +1.
 */
export function axisSign(n: { x: number; y: number; z: number }, axis: Axis): 1 | -1 {
  const v = axis === 'x' ? n.x : axis === 'y' ? n.y : n.z
  return (v >= 0 ? 1 : -1) as 1 | -1
}

/**
 * Step one voxel along a given axis in Morton space.
 */
export function mortonStepAxis(key: number, axis: Axis, dir: 1 | -1): number {
  if (axis === 'x') return dir > 0 ? incKeyX(key) : decKeyX(key)
  if (axis === 'y') return dir > 0 ? incKeyY(key) : decKeyY(key)
  return dir > 0 ? incKeyZ(key) : decKeyZ(key)
}

/**
 * Iterate 4-neighborhood in the plane orthogonal to the given axis.
 * Invokes callback with each neighbor key (order: +U, -U, +V, -V).
 * No allocations.
 */
export function forEachPlaneNeighborKey(key: number, axis: Axis, fn: (nk: number) => void): void {
  if (axis === 'x') {
    fn(incKeyY(key)); fn(decKeyY(key)); fn(incKeyZ(key)); fn(decKeyZ(key));
  } else if (axis === 'y') {
    fn(incKeyX(key)); fn(decKeyX(key)); fn(incKeyZ(key)); fn(decKeyZ(key));
  } else {
    fn(incKeyX(key)); fn(decKeyX(key)); fn(incKeyY(key)); fn(decKeyY(key));
  }
}

/**
 * Convenience: step forward along the dominant axis of a normal.
 */
export function frontNeighborKeyFromNormal(key: number, normal: { x: number; y: number; z: number }): number {
  const ax = dominantAxis(normal)
  const s = axisSign(normal, ax)
  return mortonStepAxis(key, ax, s)
}

/**
 * Extract a single coordinate from a Morton key by axis.
 */
export function axisCoordFromKey(key: number, axis: Axis): number {
  return axis === 'x' ? parseKeyX(key) : axis === 'y' ? parseKeyY(key) : parseKeyZ(key)
}

/**
 * Build Morton steppers from an integer axis unit (exactly one non-zero component).
 * Returns stepPos (along the unit) and stepNeg (opposite direction), plus the axis label.
 */
export function steppersFromAxisUnit(unit: { dx: number; dy: number; dz: number }): { stepPos: (k: number) => number; stepNeg: (k: number) => number; axis: Axis } {
  if (unit.dx !== 0) {
    return {
      stepPos: unit.dx > 0 ? incKeyX : decKeyX,
      stepNeg: unit.dx > 0 ? decKeyX : incKeyX,
      axis: 'x'
    }
  }
  if (unit.dy !== 0) {
    return {
      stepPos: unit.dy > 0 ? incKeyY : decKeyY,
      stepNeg: unit.dy > 0 ? decKeyY : incKeyY,
      axis: 'y'
    }
  }
  return {
    stepPos: unit.dz > 0 ? incKeyZ : decKeyZ,
    stepNeg: unit.dz > 0 ? decKeyZ : incKeyZ,
    axis: 'z'
  }
}

/**
 * Build an encoded key delta from integer dx,dy,dz.
 * Add this to keys to shift them without decode/re-encode.
 *
 * Note: With Morton encoding, adding encoded deltas doesn't work the same way
 * as with linear encoding. We need to decode, add, and re-encode.
 * For performance, use addKeyXYZ directly instead.
 */
export const keyDeltaXYZ = (dx: number, dy: number, dz: number): number => {
  // For Morton code, we can't simply compute a delta to add
  // This function is kept for API compatibility but should not be used in hot paths
  return posKeyXYZ(dx, dy, dz)
}

/**
 * Transform a selection by a given offset
 * @param voxels - The voxels to transform
 * @param offset - The offset to apply
 * @param bounds - Optional world bounds to clip to
 */
export function offsetVoxels(
  keys: number[],
  offset: Position | null,
  bounds: Position | undefined,
  source: VoxelMap,
): VoxelMap {
  if (offset === null && keys.length === source.size) return source

  offset = offset || { x: 0, y: 0, z: 0 }

  const out = new Map<number, VoxelData>()

  const dx = offset.x, dy = offset.y, dz = offset.z

  // Fast path: no clipping
  if (!bounds) {
    for (const key of keys) {
      const v = source.get(key)
      if (!v) continue
      const x = parseKeyX(key)
      const y = parseKeyY(key)
      const z = parseKeyZ(key)
      out.set(posKeyXYZ(x + dx, y + dy, z + dz), v)
    }
    return out
  }

  // Clipping path
  const bx = bounds.x, by = bounds.y, bz = bounds.z
  for (const key of keys) {
    const x = parseKeyX(key)
    const y = parseKeyY(key)
    const z = parseKeyZ(key)
    const nx = x + dx
    const ny = y + dy
    const nz = z + dz
    if (nx < 0 || nx >= bx || ny < 0 || ny >= by || nz < 0 || nz >= bz) continue

    const v = source.get(key)
    if (!v) continue

    out.set(posKeyXYZ(nx, ny, nz), v)
  }

  return out
}

/**
 * Compute bounding box from a set of keys (positions)
 */
export function getBoundingBoxFromKeys(keys: Iterable<number>): BoundingBox | null {
  let hasAny = false
  let minX = Infinity, minY = Infinity, minZ = Infinity
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity
  for (const key of keys) {
    hasAny = true
    const x = compactBits(key)
    const y = compactBits(key >> 1)
    const z = compactBits(key >> 2)
    if (x < minX) minX = x; if (x > maxX) maxX = x
    if (y < minY) minY = y; if (y > maxY) maxY = y
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z
  }
  if (!hasAny) return null
  return {
    min: { x: minX, y: minY, z: minZ },
    max: { x: maxX, y: maxY, z: maxZ }
  }
}

/**
 * Shift a set of keys by an integer offset, optionally clipping to bounds
 */
export function shiftKeys(
  keys: Iterable<number>,
  offset: Position | null,
  bounds?: Position
): Set<number> {
  const t = offset || { x: 0, y: 0, z: 0 }
  const out = new Set<number>()
  const dx = t.x, dy = t.y, dz = t.z

  if (!bounds) {
    for (const key of keys) {
      const x = parseKeyX(key)
      const y = parseKeyY(key)
      const z = parseKeyZ(key)
      out.add(posKeyXYZ(x + dx, y + dy, z + dz))
    }
    return out
  }

  const bx = bounds.x, by = bounds.y, bz = bounds.z
  for (const key of keys) {
    const x = parseKeyX(key)
    const y = parseKeyY(key)
    const z = parseKeyZ(key)
    const nx = x + dx
    const ny = y + dy
    const nz = z + dz
    if (nx < 0 || nx >= bx || ny < 0 || ny >= by || nz < 0 || nz >= bz) continue
    out.add(posKeyXYZ(nx, ny, nz))
  }
  return out
}

/**
 * Checks if a bounding box intersects with the grid bounds
 * Returns true if any part of the box would be inside the bounds
 */
export function isBoundingBoxInBounds(
  box: BoundingBox,
  bounds: Position
): boolean {
  // If the bounding box doesn't intersect the grid at all, it's fully out of bounds
  if (box.max.x < 0 || box.min.x >= bounds.x) return false
  if (box.max.y < 0 || box.min.y >= bounds.y) return false
  if (box.max.z < 0 || box.min.z >= bounds.z) return false

  return true
}
